"use client";

import {
  Icon,
  type IconName,
  Img,
  ServiceIcons,
  TooltipWrapper,
} from "@zapier/design-system";
import classNames from "classnames";

import styles from "./ZapCard.module.css";
import { MetaProduct } from "src/server/zsl/templates/types";
import { useTemplateDetail } from "../TemplateDetail/TemplateDetailContext";
import { Action } from "src/lib/getImplementationDetails";
import { ZapBreakdownDetails } from "../TemplateDetail/types";
import { usePathname } from "next/navigation";

import {
  UserInteractionEventName,
  emitUserInteractionEvent,
} from "src/observability/tracking";

import { TemplateBookmarkIconButton } from "../TemplateBookmarkIconButton";
import { useFeatureFlag } from "../FeatureFlags";

enum ZAP_CARD_CLICK_EVENT_INTERACTION_GOALS {
  showcase_feature = "showcase_feature",
  search = "search",
}

export type ZapCardClickEventInteractionGoal =
  keyof typeof ZAP_CARD_CLICK_EVENT_INTERACTION_GOALS;

export type ZapCardSize = "small" | "large" | "fullWidth";

export type ZapCardTemplate = {
  id: string;
  name: string;
  slug: string;
  featured?: boolean;
  variants?: {
    items: {
      key: string;
      name: string;
    }[];
  };
  description?: string;
  content?: string;
  webIntentUrl?: string;
  previewImage?: {
    title: string;
    url: string;
  } | null;
  canvasImage?: {
    title: string;
    url: string;
  } | null;
  canvasUrl?: string | null;
  primaryProduct?: string;
  products: {
    items: {
      slug?: MetaProduct;
      displayName?: string;
      displayNameSingular?: string;
      iconName?: IconName;
    }[];
  };
  apps?: {
    items: {
      name?: string;
      slug?: string;
      imageUrl?: string;
      popularity?: number;
      actions?: Action[];
    }[];
  };
  categories?: { items: { slug: string; name?: string }[] };
  zapBreakdownDetails?: ZapBreakdownDetails | null;
  relatedTemplates?: {
    items: ZapCardTemplate[];
  };
  faqs?: { items: { title: string; content: string }[] } | null;
  icon?: {
    title: string;
    url: string;
  } | null;
  relatedContent?: {
    items: {
      title: string;
      description: string;
      url: string;
      image: {
        fileName: string;
        url: string;
      } | null;
    }[];
  };
  zsl?: Record<string, any>;
};

type ZapCardProps = {
  size?: ZapCardSize;
  template: ZapCardTemplate;
  clickEventInteractionGoal?: ZapCardClickEventInteractionGoal;
  onClick?: (() => void) | null;
  className?: string;
};

const LOGGED_IN_VARIANT_KEY = "logged-in";

export function ZapCard({
  size = "small",
  template,
  clickEventInteractionGoal,
  onClick,
  className,
}: ZapCardProps) {
  const uniqueServices = Array.from(new Set(template.apps?.items ?? []));
  const uniqueServiceNames = uniqueServices.map((service) => service.name);

  const products = template.products?.items ?? [];

  const isZapTemplate = products.length === 1 && products[0].slug === "zaps";

  const pathname = usePathname();
  const variant = pathname?.includes("/app/") ? LOGGED_IN_VARIANT_KEY : null;
  const name = variant
    ? (template.variants?.items.find((v) => v.key === variant)?.name ??
      template.name)
    : template.name;

  const { setSlug, setUseCase, setPrimaryProduct } = useTemplateDetail();

  const showTemplateBookmark = useFeatureFlag("templates_bookmarking");

  const handleTemplateClick = () => {
    emitUserInteractionEvent(UserInteractionEventName.templateCardClicked, {
      event_action: "click",
      option_selected: template.slug,
      ...(clickEventInteractionGoal
        ? { interaction_goal: clickEventInteractionGoal }
        : {}),
    });

    if (onClick && typeof onClick === "function") {
      onClick();
    } else {
      setUseCase((template.categories?.items || [])[0]?.slug || null);
      setSlug(template.slug);
      setPrimaryProduct(template.primaryProduct || null);
    }
  };

  return (
    <article className={classNames([styles.root, styles[size], className])}>
      <button
        className={classNames([
          styles.zapCard,
          isZapTemplate ? styles.withAppIcons : styles.withLargeImage,
        ])}
        aria-label={template.name ?? ""}
        data-testid="zap-card"
        onClick={handleTemplateClick}
      >
        <div className={styles.topOfCard}>
          {isZapTemplate ? (
            <>
              <span className={styles.serviceIconWrapper}>
                <TooltipWrapper
                  allowMultilineTooltip={true}
                  content={uniqueServiceNames.join(", ")}
                  position="southwest"
                  shouldCorrectPosition={false}
                  tooltipId={`services-${template.slug}`}
                >
                  {() => (
                    <ServiceIcons
                      aria-label={`Zap uses ${uniqueServiceNames.join(", ")}`}
                      services={uniqueServices.map((service) => ({
                        name: service.name ?? "",
                        src: service.imageUrl ?? "",
                      }))}
                      size="medium"
                      maxServices={3}
                    />
                  )}
                </TooltipWrapper>
              </span>
            </>
          ) : (
            <Img
              src={
                template.previewImage?.url ??
                "https://images.ctfassets.net/0sppvm4cmdq7/42L9dKclk2wI6gI1Lgas4K/020b75d5c6590d7b4ea21e78a3f6e690/GenericThumbnail.png"
              }
              alt={template.previewImage?.title ?? ""}
              objectFit="cover"
              objectPosition="top left"
              borderRadius="none"
              isFullWidth
            />
          )}
        </div>
        <div className={styles.metadataWrapper}>
          <span className={styles.title}>{name}</span>

          <span className={styles.tag}>
            <TooltipWrapper
              allowMultilineTooltip={true}
              content={products
                .map((product) => product.displayName ?? product.slug)
                .join(", ")}
              position="northwest"
              shouldCorrectPosition={false}
              tooltipId={`products-${template.slug}`}
            >
              {() => (
                <span className={styles.tagIcons}>
                  {products.map((product) => (
                    <Icon
                      key={`key-${product.slug}`}
                      name={product.iconName as IconName}
                      color="BrandOrange"
                      size={14}
                    />
                  ))}
                </span>
              )}
            </TooltipWrapper>

            <span className={styles.tagText} data-testid="products">
              {products.length > 1 ? (
                <>
                  Multi-product
                  <span className={styles.hideOnMobile}> solution</span>
                </>
              ) : products.length === 1 ? (
                <>{products[0].displayName}</>
              ) : null}
            </span>
          </span>
        </div>
      </button>
      {showTemplateBookmark && (
        <div className={styles.bookmarkButtonContainer}>
          <TemplateBookmarkIconButton
            templateId={template.id}
            templateSlug={template.slug}
            isZapTemplate={isZapTemplate}
          />
        </div>
      )}
    </article>
  );
}
